/**
 * Session state and the six-state auth guard resolution.
 *
 * docs/30-interfaces/frontend.md section 3 is emphatic that this must distinguish six states,
 * not two, and that the client must not infer any of them. It does not need to: apps/api/src/
 * auth.ts:36 returns all six on GET /me, so each state below maps to a field the server owns.
 *
 * Inferring "MFA is enrolled" from the absence of a challenge is the exact bug the doc warns
 * about - it breaks the moment an operator resets MFA mid-session.
 */
import { useQuery } from '@tanstack/react-query';
import { ApiError, api, type Realm } from '@/lib/api';
import type { Profile } from '@/lib/contract';

export type GuardState = 'unauthenticated' | 'mfa_setup' | 'mfa_challenge' | 'backup_codes' | 'onboarding' | 'cleared';

export function resolveGuardState(profile: Profile | null): GuardState {
  if (!profile) return 'unauthenticated';

  // `mfa_verified` is the server's verdict on whether MFA gates this session; `mfa_enrolled`
  // only says which screen resolves it.
  //
  // This used to note that a session could arrive already verified while unenrolled, and treated
  // that as a deployment choice the client must not second-guess. It was not a choice: login set
  // `mfa_verified` to `!user.mfa_secret`, so an account that had never enrolled was born verified
  // and the guard admitted it everywhere. The server no longer infers it (INV-83), so an
  // unenrolled session is unverified, lands on `mfa_setup`, and the reading below is now the
  // whole truth rather than a workaround for the API's.
  if (!profile.mfa_verified) {
    return profile.mfa_enrolled ? 'mfa_challenge' : 'mfa_setup';
  }
  if (!profile.backup_codes_acknowledged) return 'backup_codes';
  // Operators have no onboarding flow; only vendors do -- and only where the deployment actually
  // gates on it. This used to test the status alone, which sent every freshly invited vendor to the
  // onboarding screen even when the server's own gate was off and would have admitted them
  // everywhere. `onboarding_required` is that gate, reported by the server rather than guessed at.
  if (profile.realm === 'vendor' && profile.onboarding_required && profile.onboarding_status !== 'approved')
    return 'onboarding';
  return 'cleared';
}

/**
 * Where each unresolved state must be answered.
 *
 * This lives beside `resolveGuardState` rather than inside the guard component because the two
 * halves have to agree: the guard reads a state to decide where to send someone, and it reads the
 * same mapping again to recognise that they have arrived. Splitting them across two files is how
 * the interstitials ended up mounted outside the guard, each one invalidating `/me` and waiting
 * for a redirect that nothing was left to issue.
 */
export function guardDestination(state: Exclude<GuardState, 'cleared'>, realm: Realm, loginPath: string): string {
  const base = realm === 'admin' ? '/admin' : '/v';
  switch (state) {
    case 'unauthenticated':
      return loginPath;
    case 'mfa_setup':
      return `${base}/mfa/setup`;
    case 'mfa_challenge':
      return `${base}/mfa/challenge`;
    case 'backup_codes':
      return `${base}/mfa/backup-codes`;
    case 'onboarding':
      return `${base}/onboarding`;
  }
}

/**
 * The screens that exist only to resolve a state. A cleared user standing on one has just finished
 * it, and is sent to the portal; `/mfa/reenroll` is deliberately absent, because that one is a
 * voluntary errand a cleared user is entitled to be on.
 */
export function isInterstitial(pathname: string, realm: Realm): boolean {
  const base = realm === 'admin' ? '/admin' : '/v';
  return (
    pathname === `${base}/mfa/setup` ||
    pathname === `${base}/mfa/challenge` ||
    pathname === `${base}/mfa/backup-codes` ||
    pathname === `${base}/onboarding`
  );
}

/**
 * Only a 401 means "not signed in". Everything else -- a 500, a dropped connection, a proxy
 * hiccup -- is a failure to *find out*, and must not be reported as a signed-out session: with
 * `retry: false` that turned one transient blip into a logout, which is the client inferring an
 * auth state the server never stated. The guard renders those as an error instead.
 */
export function useProfile(realm: Realm) {
  return useQuery({
    queryKey: ['profile', realm],
    queryFn: async () => {
      try {
        return await api<Profile>('/me', { realm });
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    // A 401 resolves rather than rejects, so the only thing left to retry is a genuine transport
    // or server failure -- worth one retry before showing the user anything.
    retry: 1,
    staleTime: 10_000
  });
}
